<template>
	<div class="content-wrapper">
		<!-- Content -->
		<div class="container flex-grow-1 container-p-y">
			<!-- Stats Cards -->
			<div class="row g-6 mb-6" v-if="isManager">
				<div class="col-12 col-sm-6 col-xl">
					<router-link :to="{ name: 'list-employee' }" class="text-reset text-decoration-none">
						<div class="card h-100 stats-card-clickable">
							<div class="card-body">
								<div class="d-flex align-items-center gap-3">
									<div class="avatar">
										<span class="avatar-initial rounded bg-label-primary">
											<i class="ri-group-line ri-24px"></i>
										</span>
									</div>
									<div>
										<h4 class="mb-0">{{ stats.totalEmployees }}</h4>
										<small class="text-muted">{{ $t('dashboard.stats.totalEmployees') }}</small>
									</div>
								</div>
							</div>
						</div>
					</router-link>
				</div>
				<div class="col-12 col-sm-6 col-xl">
					<router-link :to="{ name: 'list-trainings', query: { status: 'in_progress' } }" class="text-reset text-decoration-none">
						<div class="card h-100 stats-card-clickable">
							<div class="card-body">
								<div class="d-flex align-items-center gap-3">
									<div class="avatar">
										<span class="avatar-initial rounded bg-label-info">
											<i class="ri-play-circle-line ri-24px"></i>
										</span>
									</div>
									<div>
										<h4 class="mb-0">{{ stats.trainingsInProgress }}</h4>
										<small class="text-muted">{{ $t('dashboard.stats.trainingsInProgress') }}</small>
									</div>
								</div>
							</div>
						</div>
					</router-link>
				</div>
				<div class="col-12 col-sm-6 col-xl">
					<router-link :to="{ name: 'list-trainings', query: { status: 'succeeded' } }" class="text-reset text-decoration-none">
						<div class="card h-100 stats-card-clickable">
							<div class="card-body">
								<div class="d-flex align-items-center gap-3">
									<div class="avatar">
										<span class="avatar-initial rounded bg-label-success">
											<i class="ri-checkbox-circle-line ri-24px"></i>
										</span>
									</div>
									<div>
										<h4 class="mb-0">{{ stats.trainingsSucceeded }}</h4>
										<small class="text-muted">{{ $t('dashboard.stats.trainingsSucceeded') }}</small>
									</div>
								</div>
							</div>
						</div>
					</router-link>
				</div>
				<div class="col-12 col-sm-6 col-xl">
					<div class="card h-100 stats-card-clickable" @click="openSuccessRateModal" role="button">
						<div class="card-body">
							<div class="d-flex align-items-center gap-3">
								<div class="avatar">
									<span class="avatar-initial rounded" :class="stats.successRate > 70 ? 'bg-label-success' : stats.successRate > 40 ? 'bg-label-warning' : 'bg-label-danger'">
										<i class="ri-pie-chart-line ri-24px"></i>
									</span>
								</div>
								<div>
									<h4 class="mb-0">{{ stats.successRate }}%</h4>
									<small class="text-muted">{{ $t('dashboard.stats.successRate') }}</small>
								</div>
							</div>
						</div>
					</div>
				</div>
				<div class="col-12 col-sm-6 col-xl">
					<router-link :to="{ name: 'trainings-expiring' }" class="text-reset text-decoration-none">
						<div class="card h-100 stats-card-clickable">
							<div class="card-body">
								<div class="d-flex align-items-center gap-3">
									<div class="avatar">
										<span class="avatar-initial rounded bg-label-warning">
											<i class="ri-time-line ri-24px"></i>
										</span>
									</div>
									<div>
										<h4 class="mb-0">{{ stats.expiringIn90Days }}</h4>
										<small class="text-muted">{{ $t('dashboard.stats.expiringIn90Days') }}</small>
									</div>
								</div>
							</div>
						</div>
					</router-link>
				</div>
			</div>

			<div class="row g-6 mb-6">
				<!-- Congratulations card -->
				<div class="col-12 col-xl-8">
					<div class="card h-100">
						<div class="d-flex align-items-center row h-100">
							<div class="col-md-6 order-2 order-md-1">
								<div class="card-body">
									<h4 class="card-title mb-4" v-if="profile">
										{{ $t('dashboard.greeting') }} <span class="fw-bold">{{ profile.firstName }}!</span>
									</h4>
									<p class="mb-0">{{ $t('dashboard.welcomeMessage') }}</p>
									<br/>
									<router-link to="/trainings/create" class="btn btn-primary" v-if="isManager">
										{{ $t('dashboard.discoverTrainings') }}
									</router-link>
								</div>
							</div>
							<div class="col-md-6 text-center text-md-end order-1 order-md-2">
								<div class="card-body pb-0 px-0 pt-2">
									<img
										src="@/assets/user-home.png"
										height="186"
										class="scaleX-n1-rtl"
										alt="View Profile"/>
								</div>
							</div>
						</div>
					</div>
				</div>

				<!-- Trainee satisfaction card -->
				<div class="col-12 col-xl-4" v-if="isManager">
					<div class="card h-100 satisfaction-card">
						<div class="card-body d-flex flex-column align-items-center justify-content-center text-center">
							<h5 class="card-title mb-4 text-center">{{ $t('dashboard.stats.satisfaction') }}</h5>

							<div class="satisfaction-gauge">
								<svg viewBox="0 0 200 120" class="gauge-svg">
									<path
										class="gauge-bg"
										d="M 20 100 A 80 80 0 0 1 180 100"
									/>
									<path
										class="gauge-fill"
										d="M 20 100 A 80 80 0 0 1 180 100"
										:stroke-dasharray="gaugeLength"
										:stroke-dashoffset="gaugeOffset"
									/>
								</svg>
								<div class="gauge-content">
									<div class="gauge-value" v-if="satisfaction.responsesCount >= 10">
										<span class="gauge-number">{{ satisfactionPercentage }}</span>
										<span class="gauge-max">%</span>
									</div>
									<div class="gauge-value text-muted" v-else>—</div>
								</div>
							</div>

							<p class="text-muted mb-0 mt-3" v-if="satisfaction.responsesCount >= 10">
								{{ $t('dashboard.satisfactionSubtitle', { count: satisfaction.responsesCount }) }}
							</p>
							<p class="text-muted mb-0 mt-3" v-else>
								{{ $t('dashboard.satisfactionPending', { count: satisfaction.responsesCount }) }}
							</p>
						</div>
					</div>
				</div>
			</div>
		</div>
		<!-- / Content -->

		<!-- Success Rate Info Modal -->
		<div class="modal fade" id="successRateInfoModal" tabindex="-1" aria-hidden="true">
			<div class="modal-dialog modal-dialog-centered" role="document">
				<div class="modal-content">
					<div class="modal-header">
						<h5 class="modal-title">{{ $t('dashboard.stats.successRateModal.title') }}</h5>
						<button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
					</div>
					<div class="modal-body">
						<p>{{ $t('dashboard.stats.successRateModal.intro') }}</p>
						<div class="alert alert-info mb-4">
							<strong>{{ $t('dashboard.stats.successRateModal.formulaLabel') }}</strong><br/>
							<code>{{ $t('dashboard.stats.successRateModal.formula') }}</code>
						</div>
						<ul class="mb-0">
							<li>{{ $t('dashboard.stats.successRateModal.pointExcluded') }}</li>
						</ul>
					</div>
					<div class="modal-footer">
						<button type="button" class="btn btn-primary" data-bs-dismiss="modal">
							{{ $t('dashboard.stats.successRateModal.close') }}
						</button>
					</div>
				</div>
			</div>
		</div>

		<div class="content-backdrop fade"></div>
	</div>
</template>

<script>
import HTTP from "@/axios";

export default {
	name: "DashboardPage",

	data() {
		return {
			profile: null,
			company: null,
			stats: {
				totalEmployees: 0,
				trainingsInProgress: 0,
				trainingsSucceeded: 0,
				successRate: 0,
				expiringIn90Days: 0,
			},
			satisfaction: {
				averageOverall: 0,
				responsesCount: 0,
			}
		}
	},

	async mounted() {
		this.fetchProfile();
		this.fetchCompany();
		this.fetchStats();
		this.fetchSatisfaction();
	},

	computed: {
		isManager() {
			return this.profile?.role === "manager";
		},
		gaugeLength() {
			return Math.PI * 80;
		},
		gaugeOffset() {
			const pct = this.satisfaction.responsesCount >= 10
				? Math.max(0, Math.min(1, this.satisfaction.averageOverall / 5))
				: 0;
			return this.gaugeLength * (1 - pct);
		},
		satisfactionPercentage() {
			return Math.round((this.satisfaction.averageOverall / 5) * 100);
		}
	},


	methods: {
		async fetchCompany() {
			try {
				const response = await HTTP(`/company`);
				this.company = response.data.company;
			} catch (error) {
				// Récupérer le message d'erreur de l'API ou définir un message par défaut
				const errorMessage = error?.response?.data?.message || this.$t('errors.generic');

				alert(errorMessage);

				// Log détaillé dans la console (utile en développement)
				console.error("Erreur API:", error);
			}
		},
		openSuccessRateModal() {
			const modalElement = document.getElementById("successRateInfoModal");
			if (!modalElement) return;
			let modal = window.bootstrap.Modal.getInstance(modalElement);
			if (!modal) modal = new window.bootstrap.Modal(modalElement);
			modal.show();
		},
		async fetchStats() {
			try {
				const response = await HTTP(`/dashboard/stats`);
				this.stats = response.data.stats;
			} catch (error) {
				console.error("[fetchStats] error:", error);
			}
		},
		async fetchSatisfaction() {
			try {
				const response = await HTTP(`/feedback/satisfaction`);
				this.satisfaction = response.data;
			} catch (error) {
				console.error("[fetchSatisfaction] error:", error);
			}
		},
		async fetchProfile() {
			try {
				const response = await HTTP(`/user/profile`);
				this.profile = response.data.profile;
			} catch (error) {
				// Récupérer le message d'erreur de l'API ou définir un message par défaut
				const errorMessage = error?.response?.data?.message || this.$t('errors.generic');

				alert(errorMessage);

				// Log détaillé dans la console (utile en développement)
				console.error("Erreur API:", error);
			}
		},
	}
};
</script>

<style scoped>
/* Ajoutez des styles spécifiques à la page Dashboard */
.dashboard {
	padding: 1rem;
}

.card-title {
	font-size: 1.2rem;
}

.card-text {
	margin: 0;
}

.stats-card-clickable {
	transition: transform 0.15s ease, box-shadow 0.15s ease;
	cursor: pointer;
}
.stats-card-clickable:hover {
	transform: translateY(-2px);
	box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
}

.satisfaction-gauge {
	position: relative;
	width: 220px;
	max-width: 100%;
	font-family: var(--bs-body-font-family);
}

.gauge-svg {
	width: 100%;
	display: block;
}

.gauge-bg {
	fill: none;
	stroke: rgba(var(--bs-primary-rgb), 0.12);
	stroke-width: 10;
	stroke-linecap: round;
}

.gauge-fill {
	fill: none;
	stroke: rgb(var(--bs-primary-rgb));
	stroke-width: 10;
	stroke-linecap: round;
	transition: stroke-dashoffset 0.8s ease;
}

.gauge-content {
	position: absolute;
	left: 0;
	right: 0;
	bottom: 10%;
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
}

.gauge-value {
	display: flex;
	align-items: baseline;
	gap: 2px;
	font-family: var(--bs-body-font-family);
}

.gauge-number {
	font-size: 1.82rem;
	font-weight: 700;
	color: var(--bs-heading-color, var(--bs-body-color));
	line-height: 1;
}

.gauge-max {
	font-size: 0.7rem;
	color: var(--bs-secondary-color);
	font-weight: 500;
}
</style>
